/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import type React from "react";
import { useTranslation, untranslated } from "@i18n";
import axios from "axios";
import { useCallback, useMemo, useRef, useState, useEffect, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { deleteSchedule, saveScheduleData } from "../../../actions/task";
import { spacesDataUrl, ganttListUrl } from "../../../services/dataManager";
import { convertDate } from "../../../services/luxonHelper";
import ScheduleDetailsSidebar from "./ScheduleDetailsSidebar/ScheduleDetailsSidebar";
import ToolTipComponent from "../../../cubeComponent/html/tooltip";
import ButtonComponent from "../../../cubeComponent/form/ButtonComponent";
import ModalStateDialogComponent from "../../../cubeComponent/html/modal/ModalWithState";
import { getScheduleUrl } from "@Features/projects/utils/projectUrls";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import SlickGrid1 from "../../../cubeComponent/html/SlickGrid1";
import { typedColumns, type GridRowBase, GridClickEvent } from "../../../cubeComponent/html/SlickGrid1";
import _ from "lodash";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import axiosWrapper from "../../../services/axiosWrapper";
import { RiFileInfoLine } from "react-icons/ri";
import Text from "@CubeComponents/html/Text/Text";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import RadioButtonComponent from "@CubeComponents/Mui_Form/RadioButton";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { PermissionFlow } from "@Features/estimation";
import { defaultModulesMapper, folderPermissions } from "@Helpers/constants";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import NoDataSvg from "@CubeComponents/html/NoData";
import NoDataScheduleSvg from "../../../assets/icons/svg/NoScheduleList.svg";
import IconComponent from "@CubeComponents/icons";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { ArrowDirection } from "@WebTypes/scheduleDomain";
import type { DeleteScheduleRequest } from "@ServerTypes/tasks/tasks.schema";
import type { ScheduleGridRef } from "../interface";
import type { ComponentProps } from "react";

/**
 * Row type for schedule list data. `_id`/`spaceId` narrow the grid row base's loose `RowId` to the
 * 24-hex ids the list endpoint serves, so the delete/edit/navigation paths below need no assertion;
 * `_id` stays optional because {@link GridRowBase} keeps it so for the grid's own synthetic rows.
 */
interface ScheduleRow extends GridRowBase {
  /** `processData` replaces the stored numeric counter with the padded "SCH-NNNN" display label. */
  counter?: number | string;
  title?: string;
  description?: string;
  space?: { spaceName?: string };
  startDate?: number;
  endDate?: number;
  duration?: number;
  createdDate?: number;
  createdBy?: { _id?: string; fullName?: string; name?: string };
  createdByName?: string;
  _id?: ObjectIdString;
  spaceId?: ObjectIdString;
  users?: ScheduleRowUser[];
}

interface ScheduleRowUser {
  id: string;
  permissions?: number[];
}

interface ScheduleListProps {
  /** Embedded mode: hides the page chrome and the toolbar, used by the pickers. */
  showOnlyTable?: boolean;
  /** Lets an embedding picker enable its own "Link" button when exactly one row is selected. */
  linkScheduleButtonStateEnable?: (enabled: boolean) => void;
  spaceId?: ObjectIdString;
  enableCheckboxSelector?: boolean;
  /**
   * DEAD PROP: passed by `contractSchedule`'s schedule picker but read nowhere in this component.
   * Declared so that call site keeps type-checking; dropping it there would be a behaviour-neutral
   * cleanup.
   */
  contractId?: string;
}

/**
 * The user/role map `PermissionFlow` emits, taken from its own prop so the two cannot drift apart.
 * The values are the NUMERIC `folderRoles()` ids (or `""` while no role is picked), which is what
 * the file-tree seeder behind /schedule/saveScheduleData reads them back as.
 */
type ScheduleUsersRoles = Parameters<ComponentProps<typeof PermissionFlow>["getUsersCallBack"]>[0];

/** The create/edit schedule form buffer. */
interface ScheduleFormData {
  _id?: ObjectIdString;
  title: string;
  description: string;
  spaceId: string;
  projectId?: string;
  inheritPermission?: boolean;
  usersRoles?: ScheduleUsersRoles;
}

/**
 * The DataView `onSelectedRowIdsChanged` CustomEvent SlickGrid1 forwards here, narrowed to the
 * members this file reads. `grid.selectedRows`, `SlickGrid.data` and `SlickDataView.rows` are all
 * `protected` on the vendor classes (see @slickgrid-universal/common slickGrid.d.ts:34,228 and
 * slickDataview.d.ts:39), which is why they are re-declared rather than reached through the public
 * type — the same thing `ScheduleGridRef` does for this module's grids.
 */
type ScheduleSelectionEvent = {
  detail: {
    args: {
      rows?: number[];
      grid: { selectedRows: number[]; data: { rows: ScheduleRow[] } };
    };
  };
};

const ScheduleList = forwardRef<ScheduleGridRef<ScheduleRow>, ScheduleListProps>(
  ({ showOnlyTable = false, linkScheduleButtonStateEnable, spaceId, enableCheckboxSelector = true }, ref) => {
    const { t } = useTranslation();
    const projectData = useSelector((state: RootState) => state.projectData);
    const userDetails = useSelector((state: RootState) => state.userDetails);
    const { RESOURCE_DELETE, RESOURCE_MODIFY } = folderPermissions();
    const currentUserId = userDetails._id?.toString();

    const userHasResourcePermission = (row: ScheduleRow, permissionId: number) =>
      Array.isArray(row.users) &&
      row.users.some((u) => u.id === currentUserId && (u.permissions ?? []).includes(permissionId));
    const standByRef = useRef<ScheduleGridRef<ScheduleRow> | null>(null);
    // `dataGrid.current` is read all over this component, so the reads need an object ref: a callback
    // ref carries no `.current`, and those reads fall back to the local one. The parent's own ref is
    // still what the grid publishes its handle to — see `getGridComponent`.
    const dataGrid = typeof ref === "function" || ref === null ? standByRef : ref;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { projectId } = useParams();

    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [scheduleModal, setScheduleModal] = useState(false);
    const [showPermissionsFlow, setShowPermissionsFlow] = useState(false);
    const initialFormValues = {
      title: "",
      description: "",
      spaceId: "",
      projectId,
      inheritPermission: true,
    };
    const [scheduleData, setScheduleData] = useState<ScheduleFormData>(initialFormValues);
    const [selectedGanttId, setSelectedGanttId] = useState("");
    const [showScheduleSideBar, setShowScheduleSideBar] = useState(false);

    const [disableDeleteSchedule, setDisableDeleteSchedule] = useState(true);
    const [disableEditSchedule, setDisableEditSchedule] = useState(true);
    const [disableShowProperties, setDisableShowProperties] = useState(true);
    const [_spacesOptions, setSpacesOptions] = useState<unknown[]>([]);

    const [saveScheduleLoader, setSaveScheduleLoader] = useState(false);
    const [deleteScheduleLoader, setDeleteScheduleLoader] = useState(false);

    const propertiesTemplate = useCallback(() => <RiFileInfoLine size={18} className="cursor-pointer" />, []);

    const toolbar = useMemo(
      () => [
        {
          name: t("Create"),
          tooltip: "Schedule Creation",
          prefixIcon: "add",
          id: "custom_create",
          disabled: false,
        },
        {
          name: <Text>{t("Delete")}</Text>,
          tooltip: "Delete",
          prefixIcon: "delete",
          id: "custom_deleteGantt",
          disabled: disableDeleteSchedule,
        },
        {
          name: <Text>{t("Edit")}</Text>,
          tooltip: "Edit Schedule",
          prefixIcon: "EditIcon",
          id: "custom_editGantt",
          disabled: disableEditSchedule,
        },
      ],
      [disableDeleteSchedule, disableEditSchedule, disableShowProperties, propertiesTemplate],
    );

    const [query] = useState({
      projectId,
      spaceId,
      IdMapping: "_id",
      params: {
        projectId,
        spaceId,
        IdMapping: "_id",
      },
    });

    const handleDeleteGantt = async () => {
      const deleteParams: DeleteScheduleRequest = {
        ganttIds: [],
      };

      if (selectedScheduleData.length >= 1) {
        const ganttData = selectedScheduleData;
        // `_id` is optional on the grid row base (its synthetic group/total rows carry none) and a
        // row without one has nothing to delete.
        ganttData.forEach((item: ScheduleRow) => {
          if (item._id) deleteParams.ganttIds.push(item._id);
        });

        setDeleteScheduleLoader(true);
        const response = await deleteSchedule(deleteParams);
        setDeleteScheduleLoader(false);
        if (response.data.status) {
          dispatch(
            showToastNotification({
              content: t("Selected schedule(s) has been deleted successfully"),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          dataGrid.current!.slickGrid.setSelectedRows([]);
        } else {
          dispatch(
            showToastNotification({
              // Raw server copy, so `untranslated()` — the sanctioned entry point for it — not `t()`.
              content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
        handleDeleteTaskModalClose();
        fetchSchedules();
        setDisableDeleteSchedule(true);
        setDisableEditSchedule(true);
        setDisableShowProperties(true);
      }
    };

    const handleDeleteTaskModalClose = () => {
      setShowDeleteConfirmationModal(false);
    };

    const toolbarClickHandler = (params: string) => {
      const selectedData = selectedScheduleData;

      if (params === "custom_create") {
        setScheduleModal(true);
      } else if (params === "custom_deleteGantt") {
        setShowDeleteConfirmationModal(true);
      } else if (params === "custom_editGantt") {
        // The "edit" toolbar button is only enabled with exactly one row selected, and every stored
        // schedule carries these fields.
        setScheduleData({
          title: selectedData[0]!.title!,
          description: selectedData[0]!.description!,
          spaceId: selectedData[0]!.spaceId!,
          projectId,
          _id: selectedData[0]!._id,
        });
        setEditMode(true);
        setScheduleModal(true);
      } else if (params === "properties") {
        openScheduleSidebar(selectedScheduleData);
      }
    };

    const openSideBarWithArrowNavigation = (condition?: ArrowDirection) => {
      const dataSource = dataGrid.current!.dataView.filteredItems;
      dataSource.forEach((item: ScheduleRow, index: number) => {
        if (item._id != selectedGanttId) return;

        let targetIndex: number;
        if (condition == "forward") {
          targetIndex = index == dataSource.length - 1 ? 0 : index + 1;
        } else if (condition == "backward") {
          targetIndex = index == 0 ? dataSource.length - 1 : index - 1;
        } else {
          return;
        }

        // `_id` is optional on the grid row base; there is nothing to navigate to without one.
        const gantId = dataSource[targetIndex]?._id;
        if (!gantId) return;
        setSelectedGanttId(gantId);
        dataGrid.current!.dataView.setSelectedIds([gantId]);
      });
    };

    const [selectedScheduleData, setSelectedScheduleData] = useState<ScheduleRow[]>([]);

    const recordClick = (
      args: Parameters<NonNullable<ComponentProps<typeof SlickGrid1>["onSelectedRowIdsChanged"]>>[0],
    ) => {
      if (!dataGrid.current) return;

      // SlickGrid1 declares this prop as slickgrid's opaque `SlickEventArgs` (`Record<string,
      // unknown>`) while what it dispatches is the DataView CustomEvent — a mismatch its own code
      // documents at cubeComponent/html/SlickGrid1/index.tsx (`KNOWN MISMATCH`, same eslint-disable).
      // Re-declaring the prop is the real fix and would re-check all 117 of its consumers.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- prop type does not describe the dispatched event; see ScheduleSelectionEvent and SlickGrid1's own note
      const selection = args as ScheduleSelectionEvent;

      let selectedRowData: ScheduleRow[] | undefined;
      const totalRowsSelected = selection.detail.args.grid.selectedRows.length;

      if (selection.detail.args.rows) {
        selectedRowData = selection.detail.args.rows.map(
          (rowIndex: number) => selection.detail.args.grid.data.rows[rowIndex]!,
        );
      }

      if (linkScheduleButtonStateEnable) {
        linkScheduleButtonStateEnable(totalRowsSelected == 1);
      }

      const canModifySelected =
        Array.isArray(selectedRowData) &&
        selectedRowData.length > 0 &&
        selectedRowData.every((row) => userHasResourcePermission(row, RESOURCE_MODIFY.id));
      const canDeleteSelected =
        Array.isArray(selectedRowData) &&
        selectedRowData.length > 0 &&
        selectedRowData.some((row) => userHasResourcePermission(row, RESOURCE_DELETE.id));

      if (totalRowsSelected === 1) {
        setDisableShowProperties(false);
        setDisableEditSchedule(!canModifySelected);
        setDisableDeleteSchedule(!canDeleteSelected);
      } else if (totalRowsSelected > 1) {
        setShowScheduleSideBar(false);
        setDisableShowProperties(true);
        setDisableEditSchedule(true);
        setDisableDeleteSchedule(!canDeleteSelected);
      } else {
        setShowScheduleSideBar(false);
        setDisableEditSchedule(true);
        setDisableShowProperties(true);
        setDisableDeleteSchedule(true);
      }

      // A selection event without `rows` used to land `undefined` in a state declared as an array,
      // which `handleDeleteGantt` then reads `.length` off unguarded. Empty selection is what an
      // event carrying no rows means.
      setSelectedScheduleData(selectedRowData ?? []);
    };

    const formatCreatedDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      const dateFormat = convertDate(Number(dataContext.createdDate), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer={false} position="bottom" content={dateFormat}>
            <span>{convertDate(Number(dataContext.createdDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>,
      );
      return container;
    };

    const formatStartDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      if (!dataContext.startDate) return "-";
      const container = document.createElement("div");
      const dateFormat = convertDate(Number(dataContext.startDate), "dateForTooltip");
      const root = createRoot(container);
      root.render(
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer={false} position="bottom" content={dateFormat}>
            <span>{convertDate(Number(dataContext.startDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>,
      );
      return container;
    };

    const formatEndDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      if (!dataContext.endDate) return "-";

      const dateFormat = convertDate(Number(dataContext.endDate), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer={false} position="bottom" content={dateFormat}>
            <span>{convertDate(Number(dataContext.endDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>,
      );
      return container;
    };

    const formatDuration = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      if (!dataContext.duration) return "-";
      return `${dataContext.duration} days`;
    };

    const saveSchedule = async () => {
      setSaveScheduleLoader(true);
      try {
        if (scheduleData.inheritPermission || showPermissionsFlow || editMode) {
          // The form buffer holds `spaceId`/`projectId` as plain strings — the Space picker hands back
          // an untyped `_id` and `projectId` comes off the route — while /schedule/saveScheduleData
          // wants 24-hex ids, so both go through the boundary check here rather than being asserted.
          const data = {
            ...scheduleData,
            title: scheduleData.title.trim(),
            description: scheduleData.description.trim(),
            spaceId: objectIdString.safeParse(scheduleData.spaceId).data,
            projectId: objectIdString.safeParse(scheduleData.projectId).data,
          };
          const res = await saveScheduleData(data);
          if (res.data.status) {
            if (data._id) {
              dispatch(
                showToastNotification({
                  content: t("edit_schedule", { scheduleName: data.title }),
                  type: TOAST_TYPE_SUCCESS,
                }),
              );
            } else {
              dispatch(
                showToastNotification({
                  content: t("create_schedule", { scheduleName: data.title }),
                  type: TOAST_TYPE_SUCCESS,
                }),
              );
            }
            fetchSchedules();
            closeScheduleModal();
          } else {
            dispatch(
              showToastNotification({
                content: res.data.errorMessage ? untranslated(res.data.errorMessage) : SOMETHING_WENT_WRONG,
                type: TOAST_TYPE_ERROR,
              }),
            );
            closeScheduleModal();
          }
        } else {
          setShowPermissionsFlow(true);
        }
      } catch (error) {
        // `catch` hands back `unknown`; `isAxiosError` is a real runtime test (it reads the flag axios
        // sets), so the server's `errorMessage` is read without asserting. It is raw server copy,
        // hence `untranslated()` rather than a catalogue key.
        const errorMessage = axios.isAxiosError<{ errorMessage?: string }>(error)
          ? error.response?.data.errorMessage
          : undefined;
        dispatch(
          showToastNotification({
            content: errorMessage ? untranslated(errorMessage) : SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
        closeScheduleModal();
      } finally {
        setSaveScheduleLoader(false);
      }
    };

    const addedByTemplate = (
      _row: number,
      _cell: number,
      _value: unknown,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      let _id;
      if (dataContext.createdBy) {
        _id = dataContext.createdBy._id;
      } else {
        _id = "asdasdas";
      }
      const container = document.createElement("div");
      // @ts-expect-error -- an optional `string` id is passed where a branded `ObjectIdString` is required; the row carries it as a plain optional string.
      renderUserTag(container, { id: _id });
      return container;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setScheduleData({
        ...scheduleData,
        [event.target.name]: event.target.value,
      });
    };
    const _handleDropDownChange = (_event: unknown, newValue: { id?: string; _id?: string } | null) => {
      if (newValue) {
        setScheduleData({ ...scheduleData, spaceId: newValue.id || newValue._id! });
      }
    };

    const handlePermission = () => {
      setScheduleData((prev: ScheduleFormData) => ({
        ...prev,
        inheritPermission: !prev.inheritPermission,
      }));
    };

    const getBtnText = () => {
      if (editMode) {
        return t("Save");
      } else if (scheduleData.inheritPermission || showPermissionsFlow) {
        return t("Create");
      } else {
        return t("Next");
      }
    };

    const usersCallBack = (users: NonNullable<ScheduleFormData["usersRoles"]>) => {
      setScheduleData((prev: ScheduleFormData) => ({
        ...prev,
        usersRoles: users,
      }));
    };

    const createScheduleContent = () => {
      if (showPermissionsFlow) {
        return (
          <PermissionFlow
            module={defaultModulesMapper.SCHEDULES}
            spaceId={scheduleData.spaceId}
            getUsersCallBack={usersCallBack}
          />
        );
      }
      return (
        <div className="flex flex-col gap-6">
          <div className="flex items-start gap-4">
            <div className="w-[7.5rem] flex items-center gap-1">
              <Text className="font-semibold">{t("Name")}</Text>
              <span className="text-red-500">*</span>
            </div>
            <div className="flex-1">
              <TextFieldComponent
                name="title"
                value={scheduleData.title}
                onChange={handleInputChange}
                variant="outlined"
                placeholder="Schedule Name"
              />
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-[7.5rem]">
              <Text className="font-semibold">{t("Description")}</Text>
            </div>
            <div className="flex-1">
              <textarea
                className="mb-[0.4rem] w-full border border-[#19204880] px-[0.5rem] py-[0.42rem] outline-none focus:border-[#0078d4]"
                rows={5}
                minLength={1}
                maxLength={255}
                name="description"
                placeholder={t("Enter Description")}
                value={scheduleData.description}
                onChange={handleInputChange}
              />
            </div>
          </div>
          {!editMode && (
            <div className="flex items-start gap-4">
              <div className="w-[7.5rem]">
                <Text className="font-semibold">{t("Permissions")}</Text>
              </div>
              <div className="flex flex-1 flex-col gap-3">
                <RadioButtonComponent
                  change={handlePermission}
                  checked={scheduleData.inheritPermission}
                  label={t("Inherit permissions from parent folder")}
                />
                <RadioButtonComponent
                  change={handlePermission}
                  checked={!scheduleData.inheritPermission}
                  label={t("Specify permissions in the next step")}
                />
              </div>
            </div>
          )}
        </div>
      );
    };
    const createScheduleModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerBorderTop
        footerContent={
          <div className="flex gap-4">
            <ButtonComponent
              onClick={saveSchedule}
              loading={saveScheduleLoader}
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              disabled={scheduleData.spaceId == "" || scheduleData.title.trim() == ""}
            >
              <Text>{getBtnText()}</Text>
            </ButtonComponent>
            <ButtonComponent
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              onClick={showPermissionsFlow ? () => setShowPermissionsFlow(false) : closeScheduleModal}
            >
              <Text>{showPermissionsFlow ? t("Back") : t("Discard")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <div className="flex flex-row py-1 w-full justify-between items-center">
            <div className="flex items-center gap-2">
              {showPermissionsFlow && (
                <IconComponent
                  name="BackwardArrow"
                  className="cursor-pointer me-2"
                  onClick={() => setShowPermissionsFlow(false)}
                  width="1.25rem"
                  height="1.25rem"
                />
              )}
              <Text className="text-2xl font-semibold">
                {editMode
                  ? t("Edit Schedule")
                  : showPermissionsFlow
                    ? t("Specify Permission")
                    : t("Create New Schedule")}
              </Text>
            </div>
            {!showPermissionsFlow && (
              <SelectSpaces
                moduleName={defaultModulesMapper.SCHEDULES}
                projectId={projectId}
                isDisabled={editMode}
                selectedSpaceCallback={(params: { _id?: string } | null) => {
                  setScheduleData((prev: ScheduleFormData) => ({
                    ...prev,
                    spaceId: editMode ? prev.spaceId : (params?._id ?? ""),
                  }));
                }}
              />
            )}
          </div>
        }
        isVisible
        onCancel={closeScheduleModal}
        modelContent={createScheduleContent()}
        width="45%"
      />
    );

    const closeScheduleModal = () => {
      setScheduleData(initialFormValues);
      setScheduleModal(false);
      setEditMode(false);
      setShowPermissionsFlow(false);
      setSaveScheduleLoader(false);
    };

    const created = (e: GridClickEvent) => {
      if (dataGrid.current && !showOnlyTable) {
        const target = e.detail.eventData.target;

        if (
          !(
            target.classList.contains("slick-group-toggle") ||
            target.classList.contains("slick-cell-checkboxsel") ||
            target.closest(".slick-cell-checkboxsel")
          )
        ) {
          navigate(
            getScheduleUrl({
              projectId,
              // `SlickGrid.data` is the very DataView this ref exposes, and `filteredItems` is
              // `protected` on the vendor class — `ScheduleGridRef` already re-declares it as public,
              // so the clicked row is read through the ref instead of asserting over the event's grid.
              scheduleId: dataGrid.current.dataView.filteredItems[e.detail.args.row]!._id,
              selectedTab: "Gantt",
            }),
          );
        }
      }
    };

    const openScheduleSidebar = (args: ScheduleRow[]) => {
      const firstId = args[0]?._id;
      if (firstId) {
        setSelectedGanttId(firstId);
      }
      setShowScheduleSideBar((prev) => !prev);
    };

    const columnDefinitions = useMemo(
      () =>
        typedColumns<ScheduleRow>([
          {
            field: "counter",
            name: t("ID"),
            id: "counter",
            isPrimaryKey: true,
            filterType: "Text",
            maxWidth: 150,
          },
          {
            field: "title",
            name: t("Title"),
            id: "title",
            filterType: "Text",
            wrapText: true,
          },
          {
            field: "description",
            name: t("Description"),
            id: "description",
            filterType: "Text",
            wrapText: true,
          },
          {
            field: "space",
            name: t("Space Name"),
            id: "space.spaceName",
            _valueGetter: (row) => row.space?.spaceName,
            filterType: "Text",
            formatter: (_row, _cell, _value, _col, dataContext) => dataContext.space?.spaceName,
            maxWidth: 130,
          },
          {
            field: "startDate",
            name: t("Start Date"),
            id: "startDate",
            filterType: "Date",
            formatter: formatStartDate,
            _valueGetter: (row) => (row.startDate ? convertDate(Number(row.startDate), "dateForTooltip") : "-"),
          },
          {
            field: "endDate",
            name: t("End Date"),
            id: "endDate",
            filterType: "Date",
            formatter: formatEndDate,
            _valueGetter: (row) => (row.endDate ? convertDate(Number(row.endDate), "dateForTooltip") : "-"),
          },
          {
            field: "duration",
            id: "duration",
            name: t("Duration"),
            filterType: "Number",
            formatter: formatDuration,
            _excelValueGetter: (row) => (row.duration ? `${row.duration} days` : "-"),
          },
          {
            field: "createdDate",
            name: t("Created On"),
            id: "createdDate",
            filterType: "Date",
            formatter: formatCreatedDate,
            _valueGetter: (row) => (row.createdDate ? convertDate(Number(row.createdDate), "dateForTooltip") : ""),
          },
          {
            field: "createdByName",
            name: t("Created by"),
            sortable: true,
            id: "createdByName",
            filterType: "Text",
            formatter: addedByTemplate,
            _valueGetter: (row: ScheduleRow) => row.createdBy?.fullName || row.createdBy?.name || "",
            _excelValueGetter: (row: ScheduleRow) => row.createdBy?.fullName || row.createdBy?.name || "",
          },
        ]),
      [],
    );

    useEffect(() => {
      document.title = `Schedules - ${projectData.description}`;
    }, [projectData.description]);

    // Fetch spaces options
    useEffect(() => {
      const fetchSpacesOptions = async () => {
        try {
          const spacesQuery = new QueryBuilder().addParams("projectId", projectId);
          const result = (await executeQuery(spacesDataUrl, spacesQuery)) as { result?: unknown[] };
          setSpacesOptions(result.result || []);
        } catch (e) {
          console.error("Error fetching spaces options:", e);
          Promise.reject(e);
        }
      };
      if (projectId) {
        fetchSpacesOptions();
      }
    }, [projectId]);

    // SCHEMA GAP: the schedule list is served through the generic query endpoint, which has no
    // declared schema of its own; its response shape is stated once at the `axiosWrapper.post` call
    // below (`result` is the array of schedule rows) instead of being asserted here.
    const processData = (data: { data: { result?: ScheduleRow[] } }): ScheduleRow[] => {
      const items: ScheduleRow[] = data.data.result ?? [];

      return items.map((item: ScheduleRow) => ({
        ...item,
        counter: `SCH- ${String(item.counter).padStart(4, "0")}`,
        createdByName: (item.createdBy?.fullName || item.createdBy?.name || "").toLowerCase(),
      }));
    };

    const [scheduleDataset, setScheduleDataset] = useState<ScheduleRow[]>([]);

    const fetchSchedules = useCallback(async () => {
      const response = await axiosWrapper.post<{ result?: ScheduleRow[] }>(ganttListUrl, {
        value: {
          ...query,
          skip: 0,
          take: 10000,
          requiresCounts: false,
        },
      });
      setScheduleDataset(processData(response));
    }, [projectId, spaceId, query]);

    useEffect(() => {
      if (projectId) fetchSchedules();
    }, [projectId, spaceId, fetchSchedules]);

    const getGridComponent = () => (
      <SlickGrid1
        dropdownColumn={2}
        ref={dataGrid}
        toolbar={showOnlyTable ? [] : toolbar}
        toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={recordClick}
        dataset={scheduleDataset}
        idMapping="_id"
        initialSortColumnId="createdDate"
        preserveDatasetOrder
        disableChildDataFetch
        onClick={created}
        gridOptions={{
          forceFitColumns: true,
          enableSorting: true,
        }}
        selectCheckBoxOnRowClick={!enableCheckboxSelector}
        selectAtmostOneCheckBoxOnRowClick={!enableCheckboxSelector}
        disableCheckBoxSelection={!enableCheckboxSelector}
        columnDefinitions={columnDefinitions}
        enableSortingOption
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        excelExport
        propertiesPanel
      />
    );

    const noScheduleData = () => (
      <div className="flex justify-center h-full w-full items-center p-12 rounded-lg">
        <div className="flex flex-col justify-center items-center gap-5">
          <NoDataSvg svg={NoDataScheduleSvg} text1={t("No Schedule has been added yet")} />
          <ButtonComponent
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={() => toolbarClickHandler("custom_create")}
          >
            <div className="flex items-center gap-x-2">
              <IconComponent name="add" width="1rem" height="1rem" />
              <Text>{t("Create")}</Text>
            </div>
          </ButtonComponent>
        </div>
      </div>
    );

    const hasInsufficientDeletePermission =
      Array.isArray(selectedScheduleData) &&
      selectedScheduleData.some((row: ScheduleRow) => !userHasResourcePermission(row, RESOURCE_DELETE.id));

    const deleteModalContent = () => (
      <div className="flex flex-col gap-4 max-w-md">
        <span>
          <Text>{t("Are you sure you want to delete the selected Schedule(s)?")}</Text>
        </span>
        {hasInsufficientDeletePermission && (
          <div className="flex items-center gap-2 rounded-lg p-4 border border-gradient_color bg-communication_Completion_Lighter">
            <Text noWrap>
              {t(
                "Some of the selected Schedule(s) will not be deleted because you do not have sufficient permissions.",
              )}
            </Text>
          </div>
        )}
      </div>
    );

    const deleteConfirmDialog = () => (
      <ModalStateDialogComponent
        footerContent={
          <div className="flex gap-4">
            <ButtonComponent
              onClick={handleDeleteGantt}
              buttonType="gradientButton"
              loading={deleteScheduleLoader}
              disabled={deleteScheduleLoader}
            >
              <Text>{t("Confirm")}</Text>
            </ButtonComponent>
            <ButtonComponent
              onClick={handleDeleteTaskModalClose}
              buttonType="borderButton"
              disabled={deleteScheduleLoader}
            >
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        }
        onCancel={handleDeleteTaskModalClose}
        headerTitle={
          <span>
            <Text>{t("Delete Schedule(s)")}</Text>
          </span>
        }
        isVisible={showDeleteConfirmationModal}
        modelContent={deleteModalContent()}
        footerBorderTop
      />
    );

    return showOnlyTable ? (
      getGridComponent()
    ) : (
      <div className="flex h-full w-full overflow-auto">
        {scheduleModal ? createScheduleModal() : null}
        {showDeleteConfirmationModal && deleteConfirmDialog()}

        <GridPageLayout showHeader={true} title="Schedules" description="List of all the schedules in this project">
          {scheduleDataset.length > 0 ? getGridComponent() : noScheduleData()}
        </GridPageLayout>
        {showScheduleSideBar && (
          <div className="flex flex-1 min-w-[30%] max-w-[40%] mx-2">
            <ScheduleDetailsSidebar
              openSideBarWithArrowNavigation={openSideBarWithArrowNavigation}
              setScheduleSideBar={setShowScheduleSideBar}
              ganttId={selectedScheduleData[0]?._id}
              selectedCount={Array.isArray(selectedScheduleData) ? selectedScheduleData.length : 0}
            />
          </div>
        )}
      </div>
    );
  },
);

ScheduleList.displayName = "ScheduleList";
export default ScheduleList;
